import { Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ToggleInput from '../FormInputs/ToggleInput';
import StaffInfoModal from './StaffInfoModal';

BaseStaffInfoField.propTypes = {
  fieldName: PropTypes.string,
  fieldAttributes: PropTypes.object,
  toggleFieldName: PropTypes.string,
  toggleLabel: PropTypes.string,
  showToggle: PropTypes.bool,
  showModal: PropTypes.bool,
  modalLabel: PropTypes.string,
  getInputRootFieldNames: PropTypes.func,
  clearFieldNames: PropTypes.arrayOf(PropTypes.string),
  validateOnClearFieldNames: PropTypes.arrayOf(PropTypes.string),
  disableFieldsWhenToggleOn: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.func,
};

export default function BaseStaffInfoField({
  fieldName,
  fieldAttributes,
  toggleFieldName,
  toggleLabel,
  showToggle = true,
  showModal = true,
  modalLabel,
  getInputRootFieldNames,
  clearFieldNames = ['firstName', 'lastName', 'cellPhone', 'email'],
  validateOnClearFieldNames = ['cellPhone', 'email'],
  disableFieldsWhenToggleOn = true,
  onClose,
  children,
}) {
  const { control, watch, getValues, setValue } = useFormContext();
  const [isEditable, setIsEditable] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const toggleValue = showToggle ? watch(toggleFieldName) : false;

  const inputRootFieldNames = useMemo(
    () => (typeof getInputRootFieldNames === 'function' ? getInputRootFieldNames() || [] : [fieldName]),
    [getInputRootFieldNames, fieldName]
  );

  const clearStaffInfoFields = useCallback(() => {
    inputRootFieldNames.forEach((rootFieldName) => {
      clearFieldNames.forEach((field) => {
        const shouldValidate = validateOnClearFieldNames.includes(field);
        setValue(`${rootFieldName}.${field}`, null, { shouldValidate });
      });
    });
  }, [inputRootFieldNames, clearFieldNames, validateOnClearFieldNames, setValue]);

  useEffect(() => {
    if (fieldAttributes?.isEditable === false) {
      setIsEditable(false);
    } else {
      setIsEditable(disableFieldsWhenToggleOn ? !toggleValue : true);
    }

    if (!showModal || !toggleValue) {
      return;
    }

    const hasFieldsFilled = inputRootFieldNames.some((rootFieldName) =>
      clearFieldNames.some((field) => !!getValues(`${rootFieldName}.${field}`))
    );

    if (hasFieldsFilled) {
      setOpenModal(true);
      return;
    }

    clearStaffInfoFields();
  }, [
    toggleValue,
    fieldAttributes?.isEditable,
    showModal,
    inputRootFieldNames,
    clearFieldNames,
    getValues,
    disableFieldsWhenToggleOn,
    clearStaffInfoFields,
  ]);

  return (
    <Grid>
      <Grid container>
        <Grid item>
          <Typography variant="subtitle1" color="text.secondary">
            {fieldAttributes?.questionTitle}
          </Typography>
        </Grid>
        <Grid item sx={{ ml: 'auto', mr: 1 }}>
          {showToggle && (
            <Controller
              name={toggleFieldName}
              control={control}
              render={() => (
                <ToggleInput fieldAttributes={{ ...fieldAttributes, label: toggleLabel }} fieldName={toggleFieldName} />
              )}
            />
          )}
        </Grid>
      </Grid>
      {children({ isEditable })}
      {showModal && (
        <StaffInfoModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          onProceed={() => {
            clearStaffInfoFields();
            setOpenModal(false);
          }}
          onClose={() => {
            if (onClose) {
              onClose();
            } else if (toggleFieldName) {
              setValue(toggleFieldName, false);
            }
            setOpenModal(false);
          }}
          fieldAttributes={fieldAttributes}
          label={modalLabel}
        />
      )}
    </Grid>
  );
}
